import { CommonModule } from '@angular/common';
import { Component, ViewEncapsulation } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';
import { UserService } from '../../../../core/api/user.service';
import { ConfirmDialogComponent } from '../../../../components/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { HelpersService } from '../../../../core/services/helpers.service';
import { RoleService } from '../../../../core/api/role.service';
import { TeamService } from '../../../../core/api/team.service';
import moment from 'moment';
import { FileService } from '../../../../core/api/file.service';
import { LoadingService } from '../../../../core/services/loading.service';
import { PositionService } from '../../../../core/api/position.service';

@Component({
  selector: 'app-action-employee',
  standalone: true,
  imports: [
    CommonModule,
    MatSelectModule,
    MatDividerModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './action-employee.component.html',
  styleUrl: './action-employee.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ActionEmployeeComponent {
  m: any;
  id: any;
  form: FormGroup;
  roles: any[] = [];
  teams: any[] = [];
  positions: any[] = [];
  uploadStatus: boolean = false;
  imageExists: boolean = false;
  imageUrl: any;

  constructor(
    private activatedRoute: ActivatedRoute,
    private helpers: HelpersService,
    private fb: FormBuilder,
    private router: Router,
    private userService: UserService,
    private dialog: MatDialog,
    private roleService: RoleService,
    private teamService: TeamService,
    private positionService: PositionService,
    private fileService: FileService,
    private loadingService: LoadingService
  ) {
    this.m = this.activatedRoute.snapshot.queryParams['m'];
    this.id = this.activatedRoute.snapshot.queryParams['id'];
    this.form = this.fb.group({
      firstName: [null, [Validators.required]],
      lastName: [null, [Validators.required]],
      gender: [null, [Validators.required]],
      identityNumber: [null, [Validators.required]],
      birthDate: [null],
      placeOfBirth: [null],
      email: [null, [Validators.required, Validators.email]],
      phone: [null, [Validators.required]],
      employeeId: [null, [Validators.required]],
      position: [null, [Validators.required]],
      statusEmployment: [null],
      teamId: [null, [Validators.required]],
      joinDate: [null],
      endDate: [null],
      rolesId: [[], [Validators.required]],
      bankName: [null],
      bankAccountName: [null],
      bankAccountNumber: [null],
      maximumAmount: [null],
      password: [null, this.m == 'add' ? [Validators.required] : []],
      confirmPassword: [null, this.m == 'add' ? [Validators.required] : []],
      fileId: [null],
    });

    // Jalankan semua proses asynchronous secara paralel
    this.loadingService.startLoading();
    const rolesPromise = this.getRoles();
    const teamsPromise = this.getTeams();
    const positionsPromise = this.getPositions();
    const dataByIdPromise = this.getDataById();

    Promise.all([rolesPromise, teamsPromise, positionsPromise, dataByIdPromise]).finally(() => {
      this.loadingService.endLoading();
    });
  }

  getDataById(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.id) {
        this.userService.getById(this.id).then(
          (data) => {
            data['rolesId'] = data.userRoles.map((item: any) => item.role?.id);
            data['teamId'] = data.team?.id;
            this.ensurePositionOption(data.position);
            if (data.birthDate)
              data['birthDate'] = moment(data.birthDate).format('YYYY-MM-DD');
            if (data.joinDate)
              data['joinDate'] = moment(data.joinDate).format('YYYY-MM-DD');
            if (data.endDate)
              data['endDate'] = moment(data.endDate).format('YYYY-MM-DD');
            if (data.file) {
              data['fileId'] = data.file.id;
              this.fileService.download(data['fileId']).subscribe(
                (res: any) => {
                  this.imageUrl = URL.createObjectURL(res);
                  resolve();
                },
                (error) => {
                  reject(error);
                }
              );
            } else {
              resolve();
            }
            delete data.password;
            this.form.patchValue(data);
          },
          (error) => {
            this.helpers.sbError(error.message);
            reject(error);
          }
        );
      } else {
        resolve();
      }
    });
  }

  submit() {
    if (this.form.valid) {
      this.dialog
        .open(ConfirmDialogComponent, {
          width: '545px',
          maxHeight: '95vh',
          panelClass: 'confirm-dialog',
          data: {
            title: `Confirm ${
              this.m == 'add' ? 'Add New Employee' : 'Edit Employee'
            }`,
            message: `Changes to the employee cannot be undone once saved. Are you sure you want to proceed?`,
          },
        })
        .afterClosed()
        .subscribe((result: any) => {
          if (result) {
            if (this.m == 'add') {
              this.loadingService.startLoading();
              this.userService
                .insert(this.form.value)
                .then(() => {
                  this.loadingService.endLoading();
                  this.helpers.sbSuccess('Employee berhasil ditambahkan!');
                  this.router.navigate(['/dashboard/employee']);
                })
                .catch((error) => {
                  this.loadingService.endLoading();
                  this.helpers.sbError(error.message);
                });
            } else if (this.m == 'edit' && this.id) {
              this.loadingService.startLoading();
              this.userService
                .updateById(this.id, this.form.value)
                .then(() => {
                  this.loadingService.endLoading();
                  this.helpers.sbSuccess('Employee berhasil diperbarui!');
                  this.router.navigate(['/dashboard/employee']);
                })
                .catch((error) => {
                  this.loadingService.endLoading();
                  this.helpers.sbError(error.message);
                });
            }
          }
        });
    } else {
      this.helpers.sbError('Lengkapi form terlebih dahulu!');
    }
  }

  cancel() {
    this.router.navigate(['/dashboard/employee']);
  }

  getRoles(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.roleService.getAllData().then(
        (res) => {
          this.roles = res;
          resolve();
        },
        (error) => {
          this.helpers.sbError(error.message);
          reject(error);
        }
      );
    });
  }

  getTeams(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.teamService.getAllData().then(
        (res) => {
          this.teams = res;
          resolve();
        },
        (error) => {
          this.helpers.sbError(error.message);
          reject(error);
        }
      );
    });
  }

  getPositions(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.positionService.getAllData().then(
        (res) => {
          this.positions = res;
          this.ensurePositionOption(this.form.get('position')?.value);
          resolve();
        },
        (error) => {
          this.helpers.sbError(error.message);
          reject(error);
        }
      );
    });
  }

  private ensurePositionOption(positionName?: string | null): void {
    if (!positionName) return;
    const exists = this.positions.some((item: any) => item.name === positionName);
    if (!exists) {
      this.positions = [
        ...this.positions,
        {
          id: `legacy-${positionName}`,
          name: positionName,
          description: 'Legacy position',
        },
      ];
    }
  }

  uploadImage(event: any) {
    this.uploadStatus = true;
    if (event.target.files.length) {
      const file = event.target.files[0];
      if (file.type.includes('image/')) {
        this.loadingService.startLoading();
        try {
          this.fileService.upload(file).subscribe(
            (res: any) => {
              this.loadingService.endLoading();
              if (res?.body?.data) {
                const fileId = res?.body?.data?.id;
                this.loadingService.startLoading();
                this.fileService.download(fileId).subscribe(
                  (res: any) => {
                    this.loadingService.endLoading();
                    this.imageUrl = URL.createObjectURL(res);
                  },
                  (error) => {
                    this.loadingService.endLoading();
                  }
                );
                this.form.get('fileId')?.setValue(fileId);
                this.uploadStatus = false;
              }
            },
            (err: any) => {
              this.loadingService.endLoading();
              this.helpers.sbError(err.message);
              this.uploadStatus = false;
            }
          );
        } catch (err: any) {
          this.loadingService.endLoading();
          this.helpers.sbError(err.message);
          this.uploadStatus = false;
        }
      } else {
        this.helpers.sbError('Hanya diperbolehkan upload gambar!');
        this.uploadStatus = false;
      }
    } else {
      this.helpers.sbError('Tidak ada file yang akan diupload!');
      this.uploadStatus = false;
    }
  }
}
